import { ArrowUpRight, Play, ChevronDown } from "lucide-react";
import Spotlight from "./Spotlight";
import { useMagnetic } from "../lib/useMagnetic";

const HERO_BG =
  "https://images.unsplash.com/photo-1699375197446-c2ce8db47046?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBsdXh1cnklMjB0ZXh0dXJlfGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc3ODMwNjIzM3ww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ctaPrimary = useMagnetic({ strength: 0.3 });
  const ctaSecondary = useMagnetic({ strength: 0.25 });
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Background image with heavy overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-50"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/70 to-[#050505]"
      />

      {/* Mouse spotlight (premium) */}
      <Spotlight size={720} color="rgba(255,255,255,0.06)" />

      {/* Animated aurora orbs */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-32 h-[34rem] w-[34rem] rounded-full bg-white/[0.06] blur-3xl float-slow pulse-glow"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/3 -right-40 h-[28rem] w-[28rem] rounded-full bg-white/[0.04] blur-3xl float-slower"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1/3 h-[22rem] w-[22rem] rounded-full bg-white/[0.03] blur-3xl float-slow"
        style={{ animationDelay: "-7s" }}
      />

      {/* Grid lines + noise */}
      <div aria-hidden="true" className="absolute inset-0 grid-lines opacity-50" />
      <div aria-hidden="true" className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Animated scan line */}
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent scan-line pointer-events-none"
      />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-24 md:pb-32 flex flex-col min-h-[100svh] justify-center reveal-stagger">
        <div className="flex items-center gap-3 mb-8" data-testid="hero-eyebrow">
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-neutral-400 font-medium">
            Global · Creator Management
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-display text-[14vw] sm:text-[10vw] md:text-[8.5vw] leading-[0.9] tracking-[-0.04em] font-light text-white text-glow"
        >
          OUTTARAGE
          <br />
          <span className="font-medium italic">Management</span>
        </h1>

        <p
          data-testid="hero-subtitle"
          className="mt-10 md:mt-12 max-w-2xl text-lg md:text-xl text-neutral-300 leading-[1.6] font-body font-light"
        >
          Creator management for internet-first talent. Brand deals, partnerships and
          long-term brand strategy for the people shaping culture online — anywhere in
          the world.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            ref={ctaPrimary}
            href="#contact"
            data-testid="hero-cta-work"
            className="group inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors rounded-full px-8 py-4 text-sm font-medium tracking-wide will-change-transform"
          >
            Work With Us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            ref={ctaSecondary}
            href="#services"
            data-testid="hero-cta-services"
            className="group inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/20 hover:border-white/60 hover:bg-white/5 transition-colors rounded-full px-8 py-4 text-sm font-medium tracking-wide will-change-transform"
          >
            <Play className="h-3.5 w-3.5 fill-white" />
            View Services
          </a>
        </div>

        {/* Bottom meta */}
        <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 max-w-3xl border-t border-white/10 pt-12">
          {[
            { k: "Audience", v: "800K+" },
            { k: "Monthly Views", v: "100M+" },
            { k: "Active Creators", v: "11" },
            { k: "Reach", v: "Worldwide" },
          ].map((m) => (
            <div key={m.k} data-testid={`hero-meta-${m.k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-3xl md:text-4xl font-light tracking-[-0.03em] text-white">
                {m.v}
              </div>
              <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-medium">
                {m.k}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#about"
        aria-label="Scroll to about"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-neutral-500 hover:text-white transition-colors group"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] font-medium">Scroll</span>
        <ChevronDown className="h-4 w-4 scroll-indicator" strokeWidth={1.2} />
      </a>
    </section>
  );
};

export default Hero;
